import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  FolderIcon,
  DocumentTextIcon,
  Cog6ToothIcon,
  ChartBarIcon,
  PlusIcon,
  Bars3Icon,
  ChevronDoubleLeftIcon,
  TrashIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import { useChatStore } from '../hooks/useChatStore';
import { useAuthStore } from '../hooks/useAuthStore';
import { useSettingsStore } from '../hooks/useSettingsStore';
import { notify } from '../hooks/useNotificationStore';
import ConfirmDeleteModal from './ConfirmDeleteModal';

interface LayoutProps {
  children: React.ReactNode;
}

export default function Layout({ children }: LayoutProps) {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [chatToDelete, setChatToDelete] = useState<{ id: number; title: string } | null>(null);
  const [isMobile, setIsMobile] = useState(false);
  const [isHovering, setIsHovering] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { chats, fetchChats, fetchQueryTypes, deleteChat } = useChatStore();
  const { logout, user } = useAuthStore();
  const { fetchSettings } = useSettingsStore();

  useEffect(() => {
    fetchChats();
    fetchQueryTypes();
    fetchSettings();
  }, [fetchChats, fetchQueryTypes, fetchSettings]);

  // Mobile detection
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.matchMedia('(max-width: 768px)').matches);
    };

    // Initial check
    checkMobile();

    // Listen for changes
    const mediaQuery = window.matchMedia('(max-width: 768px)');
    const handleChange = (e: MediaQueryListEvent) => setIsMobile(e.matches);

    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handleChange);
      return () => mediaQuery.removeEventListener('change', handleChange);
    } else {
      // Fallback for older browsers
      mediaQuery.addListener(handleChange);
      return () => mediaQuery.removeListener(handleChange);
    }
  }, []);

  // Auto-close sidebar on navigation for mobile
  useEffect(() => {
    if (isMobile && sidebarOpen) {
      setSidebarOpen(false);
    }
  }, [location.pathname, isMobile]);

  const handleDeleteChat = async () => {
    if (!chatToDelete) return;

    try {
      await deleteChat(chatToDelete.id);
      notify.success('Chat deleted', `"${chatToDelete.title}" has been deleted.`);

      // Navigate away if we deleted the current chat
      if (location.pathname === `/chat/${chatToDelete.id}`) {
        navigate('/');
      }
    } catch (error) {
      notify.error('Failed to delete chat', 'Please try again.');
    } finally {
      setChatToDelete(null);
    }
  };

  const navigation = [
    { name: 'Projects', href: '/projects', icon: FolderIcon },
    { name: 'Documents', href: '/documents', icon: DocumentTextIcon },
    ...(!user?.is_admin ? [{ name: 'Report', href: '/report', icon: ExclamationTriangleIcon }] : []),
  ];

  return (
    <div className="h-screen bg-surface-900 flex overflow-hidden">
      {/* Desktop hover trigger area when sidebar is closed */}
      {!isMobile && !sidebarOpen && (
        <div
          className="fixed left-0 top-0 w-2 h-full z-30 bg-transparent"
          onMouseEnter={() => setIsHovering(true)}
        />
      )}

      {/* Sidebar */}
      <div
        className={`${
          sidebarOpen ? 'w-64' : 'w-0'
        } bg-surface-800 border-r border-surface-700 transition-all duration-300 overflow-hidden flex flex-col`}
        onMouseEnter={() => !isMobile && !sidebarOpen && setIsHovering(true)}
        onMouseLeave={() => !isMobile && setIsHovering(false)}
      >
        {/* Desktop hover area when sidebar is closed */}
        {!isMobile && !sidebarOpen && isHovering && (
          <div
            className="fixed left-0 top-0 w-64 h-full bg-surface-800 border-r border-surface-700 z-40 transition-all duration-200"
            onMouseEnter={() => setIsHovering(true)}
            onMouseLeave={() => setIsHovering(false)}
          >
            {/* Sidebar content for hover state */}
            <div className="flex flex-col h-full">
              {/* Logo */}
              <Link to="/" className="block p-3 border-b border-surface-700 hover:bg-surface-700/50 transition-colors">
                <h1 className="text-lg font-bold text-primary-400">Quorum</h1>
                <p className="text-[10px] text-surface-400">AI Consensus System</p>
              </Link>

              {/* Navigation - New Chat + Menu */}
              <div className="p-2 border-b border-surface-700">
                <nav className="space-y-0.5">
                  <button
                    onClick={() => navigate('/')}
                    className={`w-full flex items-center gap-2 px-2 py-1.5 rounded-lg text-sm transition-colors ${
                      location.pathname === '/'
                        ? 'bg-surface-700 text-surface-100'
                        : 'text-surface-300 hover:bg-surface-700'
                    }`}
                  >
                    <PlusIcon className="w-4 h-4" />
                    New Chat
                  </button>
                  {[
                    { name: 'Projects', href: '/projects', icon: FolderIcon },
                    { name: 'Documents', href: '/documents', icon: DocumentTextIcon },
                    ...(!user?.is_admin ? [{ name: 'Report', href: '/report', icon: ExclamationTriangleIcon }] : []),
                  ].map((item) => (
                    <Link
                      key={item.name}
                      to={item.href}
                      className={`flex items-center gap-2 px-2 py-1.5 rounded-lg text-sm transition-colors ${
                        location.pathname === item.href
                          ? 'bg-surface-700 text-surface-100'
                          : 'text-surface-300 hover:bg-surface-700'
                      }`}
                    >
                      <item.icon className="w-4 h-4" />
                      {item.name}
                    </Link>
                  ))}
                </nav>
              </div>

              {/* Chat List */}
              <div className="flex-1 overflow-y-auto p-2">
                <h3 className="text-xs font-semibold text-surface-500 uppercase tracking-wider mb-1 px-2">
                  Recent Chats
                </h3>
                <div className="space-y-0.5">
                  {chats.slice(0, 20).map((chat) => (
                    <div
                      key={chat.id}
                      className={`group flex items-center px-2 py-1 rounded-lg text-sm transition-colors ${
                        location.pathname === `/chat/${chat.id}`
                          ? 'bg-surface-700 text-surface-100'
                          : 'text-surface-300 hover:bg-surface-700'
                      }`}
                    >
                      <Link
                        to={`/chat/${chat.id}`}
                        className="flex-1 min-w-0 truncate"
                      >
                        {chat.title}
                      </Link>
                      <button
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setChatToDelete({ id: chat.id, title: chat.title });
                        }}
                        className="p-0.5 rounded opacity-0 group-hover:opacity-100 hover:bg-surface-600 text-surface-400 hover:text-red-400 transition-all ml-1"
                        title="Delete chat"
                      >
                        <TrashIcon className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              {/* Settings & Logout */}
              <div className="p-2 border-t border-surface-700 space-y-0.5">
                <Link
                  to="/settings"
                  className={`flex items-center gap-2 px-2 py-1.5 rounded-lg text-sm transition-colors ${
                    location.pathname === '/settings'
                      ? 'bg-surface-700 text-surface-100'
                      : 'text-surface-300 hover:bg-surface-700'
                  }`}
                >
                  <Cog6ToothIcon className="w-4 h-4" />
                  Settings
                </Link>
                <button
                  onClick={logout}
                  className="w-full text-left px-2 py-1.5 text-sm text-surface-400 hover:text-surface-200 transition-colors"
                >
                  Sign out{user && <span className="text-xs"> ({user.username})</span>}
                </button>
              </div>
            </div>
          </div>
        )}
        {/* Logo */}
        <Link to="/" className="block p-3 border-b border-surface-700 hover:bg-surface-700/50 transition-colors">
          <h1 className="text-lg font-bold text-primary-400">Quorum</h1>
          <p className="text-[10px] text-surface-400">AI Consensus System</p>
        </Link>

        {/* Navigation - New Chat + Menu */}
        <div className="p-2 border-b border-surface-700">
          <nav className="space-y-0.5">
            {/* New Chat */}
            <button
              onClick={() => navigate('/')}
              className={`w-full flex items-center gap-2 px-2 py-1.5 rounded-lg text-sm transition-colors ${
                location.pathname === '/'
                  ? 'bg-surface-700 text-surface-100'
                  : 'text-surface-300 hover:bg-surface-700'
              }`}
            >
              <PlusIcon className="w-4 h-4" />
              New Chat
            </button>
            {navigation.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                className={`flex items-center gap-2 px-2 py-1.5 rounded-lg text-sm transition-colors ${
                  location.pathname === item.href
                    ? 'bg-surface-700 text-surface-100'
                    : 'text-surface-300 hover:bg-surface-700'
                }`}
              >
                <item.icon className="w-4 h-4" />
                {item.name}
              </Link>
            ))}
          </nav>
        </div>

        {/* Chat List */}
        <div className="flex-1 overflow-y-auto p-2">
          <h3 className="text-xs font-semibold text-surface-500 uppercase tracking-wider mb-1 px-2">
            Recent Chats
          </h3>
          <div className="space-y-0.5">
            {chats.slice(0, 25).map((chat) => (
              <div
                key={chat.id}
                className={`group flex items-center px-2 py-1 rounded-lg text-sm transition-colors ${
                  location.pathname === `/chat/${chat.id}`
                    ? 'bg-surface-700 text-surface-100'
                    : 'text-surface-300 hover:bg-surface-700'
                }`}
              >
                <Link
                  to={`/chat/${chat.id}`}
                  className="flex-1 min-w-0 truncate"
                >
                  {chat.title}
                </Link>
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setChatToDelete({ id: chat.id, title: chat.title });
                  }}
                  className="p-0.5 rounded opacity-0 group-hover:opacity-100 hover:bg-surface-600 text-surface-400 hover:text-red-400 transition-all ml-1"
                  title="Delete chat"
                >
                  <TrashIcon className="w-3 h-3" />
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Settings & Logout */}
        <div className="p-2 border-t border-surface-700 space-y-0.5">
          <Link
            to="/settings"
            className={`flex items-center gap-2 px-2 py-1.5 rounded-lg text-sm transition-colors ${
              location.pathname === '/settings'
                ? 'bg-surface-700 text-surface-100'
                : 'text-surface-300 hover:bg-surface-700'
            }`}
          >
            <Cog6ToothIcon className="w-4 h-4" />
            Settings
          </Link>
          <button
            onClick={logout}
            className="w-full text-left px-2 py-1.5 text-sm text-surface-400 hover:text-surface-200 transition-colors"
          >
            Sign out{user && <span className="text-xs"> ({user.username})</span>}
          </button>
        </div>
      </div>

      {/* Fixed Top bar */}
      <div className={`fixed top-0 right-0 h-14 border-b border-surface-700 flex items-center px-4 bg-surface-800 z-20 transition-all duration-300 ${
        sidebarOpen ? 'left-64' : 'left-0'
      }`}>
        <button
          onClick={() => setSidebarOpen(!sidebarOpen)}
          className="p-2 rounded-lg hover:bg-surface-700 transition-colors"
          title={sidebarOpen ? 'Collapse sidebar' : 'Expand sidebar'}
        >
          {sidebarOpen ? (
            <ChevronDoubleLeftIcon className="w-5 h-5 text-surface-400" />
          ) : (
            <Bars3Icon className="w-5 h-5 text-surface-400" />
          )}
        </button>

        {/* New Chat button */}
        <div className="flex-1 flex justify-end">
          <button
            onClick={() => navigate('/')}
            className="w-8 h-8 rounded-full bg-primary-600 hover:bg-primary-700 flex items-center justify-center transition-colors"
            title="New Chat"
          >
            <PlusIcon className="w-4 h-4 text-white" />
          </button>
        </div>
      </div>

      {/* Main content */}
      <div className="flex-1 flex flex-col min-w-0 pt-14">
        {/* Content */}
        <div className="flex-1 overflow-hidden">{children}</div>
      </div>

      {/* Delete Chat Confirmation Modal */}
      <ConfirmDeleteModal
        isOpen={chatToDelete !== null}
        onClose={() => setChatToDelete(null)}
        onConfirm={handleDeleteChat}
        title="Delete Chat"
        message={`Are you sure you want to delete "${chatToDelete?.title}"? This will also permanently delete any documents associated with this chat.`}
      />
    </div>
  );
}
